import UserIcon from "../../svg/user-icon";
import { Button, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import type { EligibilityTrialOption } from "../../data-model/eligibilityOptions";
import {
  EMPTY_TRIAL_METADATA,
  getTrialDataPoints,
  getTrialTitle,
} from "../../data-model/eligibilityOptions";

/** Props for the eligibility mapping trial header. */
interface PatientEligibilityMappingHeaderProps {
  trials: EligibilityTrialOption[];
  selectedTrialValue: string;
  onTrialChange: (trialValue: string) => void;
  /** Opens the prompt that screens a patient against the selected trial. */
  onAnalyzeNewPatient: () => void;
  /** When false, hides Analyze New Patient (e.g. archived worklist view). */
  showAnalyzeNewPatient?: boolean;
}

/**
 * Header section for the Patient Eligibility Mapping worklist page.
 *
 * The identity fields come from the eligibility partition root: title and NCT
 * chip above, then phase, design type, and sponsor. Missing values stay "--".
 *
 * @input props.trials - Trial options from the eligibility options API
 * @input props.selectedTrialValue - Currently selected trial option value
 * @input props.onTrialChange - Called when the operator picks another trial
 * @input props.onAnalyzeNewPatient - Opens the analyze-patient prompt
 * @return Compact page header with selected trial metadata and placeholders
 */
const PatientEligibilityMappingHeader = ({
  trials,
  selectedTrialValue,
  onTrialChange,
  onAnalyzeNewPatient,
  showAnalyzeNewPatient = true,
}: PatientEligibilityMappingHeaderProps) => {
  const selectedTrial =
    trials.find((trial) => trial.value === selectedTrialValue) ??
    trials[0] ??
    null;
  const dataPoints = selectedTrial ? getTrialDataPoints(selectedTrial) : [];
  const title = selectedTrial ? getTrialTitle(selectedTrial) : "Select a trial";

  return (
    <div className="flex items-center justify-between gap-3 border-b border-primaryGray-15 bg-white px-3 py-2">
      <div className="header--container flex min-w-0 flex-col justify-between gap-1">
        <div className="header--main flex items-center gap-2">
          {trials.length > 1 ? (
            <label className="sr-only" htmlFor="eligibility-trial-select">
              Trial
            </label>
          ) : null}
          {trials.length > 1 ? (
            <select
              id="eligibility-trial-select"
              className="text-body font-bold text-primaryGray-1 bg-transparent"
              value={selectedTrial?.value ?? ""}
              onChange={(event) => onTrialChange(event.target.value)}
            >
              {trials.map((trial) => (
                <option key={trial.value} value={trial.value}>
                  {getTrialTitle(trial)}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-body font-bold text-primaryGray-1">
              {title}
            </div>
          )}
          <div>
            <Tags
              backgroundColor="#E3E9EC"
              label={selectedTrial?.nct_id || EMPTY_TRIAL_METADATA}
              textColor="#0F0F0F"
            />
          </div>
        </div>
        {dataPoints.length > 0 ? (
          <div className="flex flex-wrap">
            {dataPoints.map((point, index) => (
              <SingleDataPoint
                key={`${point.label}-${point.value}`}
                label={point.label}
                value={point.value}
                showDivider={index < dataPoints.length - 1}
              />
            ))}
          </div>
        ) : null}
      </div>
      {showAnalyzeNewPatient ? (
        <div className="flex h-full items-center justify-center self-center">
          <Button
            buttonType="primary"
            size="medium"
            disabled={true}
            onClick={onAnalyzeNewPatient}
          >
            <span className="inline-flex items-center justify-center gap-2">
              <UserIcon />
              Analyze New Patient
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Compact labeled metadata chip for the trial header.
 *
 * @input props.label - Field label
 * @input props.value - Field value
 * @input props.showDivider - When true, renders a trailing separator
 * @return Labeled trial metadata chip
 */
const SingleDataPoint = ({
  label,
  value,
  showDivider,
}: {
  label: string;
  value: string;
  showDivider: boolean;
}) => {
  return (
    <div className="flex items-center gap-1">
      <div className="text-small font-normal text-primaryGray-4">{label}:</div>
      <div className="text-small font-normal text-primaryGray-4 mr-2">
        {value}
      </div>
      {showDivider ? (
        <div className="h-4 w-[1px] bg-primaryGray-11 mr-2">&nbsp;</div>
      ) : null}
    </div>
  );
};

export default PatientEligibilityMappingHeader;
